/**
 * Types for the cost dashboard. Entries and the directory are read straight
 * from Rolodex and folded client-side (transforms/); the response-shaped
 * interfaces below intentionally keep the field spellings of the deleted
 * power-backend read API so the port stays line-for-line comparable with
 * the retired composers. The one remaining power-backend read is the query
 * log (`/api/v2/usage-events/queries`). The flat usage counters live on the
 * parallel `site-usage-metrics` entries (split off the cost entries so
 * power-backend can strip them from the cost payloads); joinUsageEntries
 * merges the two loads by (site, day).
 */

/** Calendar day, YYYY-MM-DD. */
export type CostIsoDay = string

export const COST_GRANULARITIES = ['day', 'week', 'month'] as const
export type CostGranularity = (typeof COST_GRANULARITIES)[number]

/** The Usage card's tabs -- per-user and raw views of the query log. Stale
 *  deep links to retired tab values degrade via the route schema's .catch. */
export const COST_DASHBOARD_TABS = ['users', 'queries'] as const
export type CostDashboardTab = (typeof COST_DASHBOARD_TABS)[number]

export const COST_CHART_VIEWS = ['trend', 'cumulative'] as const
export type CostChartView = (typeof COST_CHART_VIEWS)[number]

/** Stacking dimension for the cost timeseries chart. */
export const COST_STACK_BYS = [
  'category',
  'service',
  'site',
  'env',
  'config',
  'status',
] as const
export type CostStackBy = (typeof COST_STACK_BYS)[number]

/** The stack-by options offered in the chart toggle. 'config' and 'status'
 *  stay valid URL values so old links keep working, but are not offered. */
export const COST_STACK_BY_OPTIONS = [
  'category',
  'service',
  'site',
  'env',
] as const

/** Read-time pipeline-config status from the Rolodex directory. */
export const COST_CONFIG_STATUSES = [
  'active',
  'inactive',
  'archived',
  'unknown',
] as const
export type CostConfigStatus = (typeof COST_CONFIG_STATUSES)[number]

/**
 * The page's filter state: each dimension is a value SET (OR within a
 * dimension, dimensions AND together; undefined = no filter), applied
 * client-side over the loaded entries. Only the org tags and the day range
 * also scope the Rolodex load itself.
 */
export interface CostQueryFilters {
  org?: string[]
  site?: string[]
  start?: CostIsoDay
  end?: CostIsoDay
  // Cost-tree filters, mirroring the attribution hierarchy:
  // category/service > config > status.
  category?: string[]
  service?: string[]
  project?: string[]
  config?: string[]
  config_status?: CostConfigStatus[]
}

// Rolodex directory ---------------------------------------------------------

export interface CostOrganization {
  id: string
  name: string
}

export interface CostSite {
  id: string
  name: string
  org_id: string
}

/** Single source of truth for chart colors, legend order, and the detail
 *  table's category dots (see costConfig.ts). */
export interface CostServiceCategory {
  key: string
  label: string
  color: string
}

export interface CostPipelineConfigOption {
  id: string
  status: string
}

// Run marker ------------------------------------------------------------

/**
 * The `site-cost-metrics-run-marker` entry payload, written LAST by the
 * aggregation run, so its existence proves a complete run. Intentionally
 * only the fields the dashboard reads: zod strips the rest, so writer drift
 * in unread fields cannot break the banner or the Refresh polling.
 */
export interface CostRunMarker {
  data_through: CostIsoDay
  /** Canonical UTC timestamp (micros + trailing Z). */
  completed_at: string
  ledger_fallback: boolean
}

// Summary tiles ----------------------------------------------------------

export interface CostSummary {
  total_cost: number
  active_site_count: number
  /** Rolodex corpus totals for the scope; null when Rolodex is degraded. */
  documents_total: number | null
  pages_total: number | null
}

// Timeseries -----------------------------------------------------------

export interface CostTimeseriesBucket {
  period_start: CostIsoDay
  costs: Record<string, number>
  total: number
  /** How many series folded into this bucket's Other value (0 = none). */
  other_count: number
}

/** The chart's bucket drill-down, scoping the tables to one bucket. */
export interface CostBucketDrill {
  periodStart: string
  granularity: CostGranularity
}

export interface CostTimeseriesSeries {
  key: string
}

export interface CostTimeseriesResponse {
  /** One entry per stacked series in render order; key matches buckets[].costs. */
  series: CostTimeseriesSeries[]
  buckets: CostTimeseriesBucket[]
}

// Detail ---------------------------------------------------------------

export interface CostDetailRow {
  org_name: string
  site_id: string
  site_name: string
  env: string
  project_id: string
  service: string
  service_category: string
  /**
   * hash12 | 'unknown-config' (history before the label deploy -- can never
   * be attributed) | null (config axis has no meaning for this cost).
   */
  config: string | null
  config_status: CostConfigStatus | null
  /** Attribution-quality ladder: direct_label > page/query driver > rest. */
  method: string
  is_shared: boolean
  allocation_fraction: number
  allocation_rationale: string
  estimated: boolean
  cost: number
}

export interface CostDetailResponse {
  rows: CostDetailRow[]
  total_row_count: number
  returned_row_count: number
  truncated: boolean
}

// Usage timeseries ------------------------------------------------------

/**
 * Usage overlay metrics. `queries` counts doc-search queries (agentic
 * searches emit one too -- adding them would double-count);
 * `external_queries` is the same for non-Gecko users only; `sites` is the
 * active-site count per bucket.
 */
export type CostUsageMetric =
  | 'queries'
  | 'external_queries'
  | 'pages'
  | 'documents'
  | 'sites'

export interface CostUsagePoint {
  period_start: CostIsoDay
  value: number
}

export interface CostUsageTimeseriesResponse {
  metric: CostUsageMetric
  /**
   * Metric total BEFORE the selected window (nonzero for pages/documents).
   * Cumulative view = baseline + running sum of `points`.
   */
  baseline: number
  points: CostUsagePoint[]
}

// Usage audience ---------------------------------------------------------

/** Audience slice for the Usage card. Internal = Gecko accounts
 *  (`is_internal === true` on the log rows); everything else -- including
 *  unknown -- reads external, matching the Internal badge semantics. */
export const COST_USAGE_AUDIENCES = ['all', 'internal', 'external'] as const
export type CostUsageAudience = (typeof COST_USAGE_AUDIENCES)[number]

// /queries (the one surviving power-backend read) -------------------------

export type CostQueryKind = 'search' | 'agentic'

export interface CostQueryLogRow {
  /** ISO timestamp of the query event. */
  timestamp: string
  /** Raw site id -- display names resolve from the Rolodex directory. */
  site_id: string
  user_email: string | null
  /** Whether the user is a Gecko-internal account; null when unknown. */
  is_internal: boolean | null
  query: string | null
  kind: CostQueryKind
}

export interface CostQueryLogResponse {
  /** Newest first, capped by the `limit` param. */
  rows: CostQueryLogRow[]
  total_row_count: number
  truncated: boolean
}

// LLM ------------------------------------------------------------------

/** One row per (model, mode) across every site in scope. Mode stays its own
 *  axis: batch and realtime pricing differ, so a merged $/1M would be
 *  meaningless. */
export interface CostLlmRow {
  model_name: string
  is_batch: boolean
  calls: number
  tokens: number
  cost: number
  /** Null when the row has cost but no token counts -- render as em dash. */
  cost_per_1m_tokens: number | null
}

export interface CostLlmResponse {
  rows: CostLlmRow[]
}

// Breakdown ------------------------------------------------------------

/** The Costs card's group toggle, in render order: the two table groups
 *  bracket the fold dimensions, and 'site' stays the route default. */
export const COST_BREAKDOWN_GROUPS = [
  'line_item',
  'category',
  'service',
  'env',
  'config',
  'status',
  'site',
  'model',
] as const
export type CostBreakdownGroup = (typeof COST_BREAKDOWN_GROUPS)[number]

/** The groups that swap in whole tables (detail / LLM) instead of running
 *  the ranked fold. `CostBreakdownFoldGroup` and `isFoldGroup` both derive
 *  from this const, so extending the split is a one-line edit here. */
export const COST_BREAKDOWN_TABLE_GROUPS = ['line_item', 'model'] as const

/** The groups the ranked breakdown fold can group over. */
export type CostBreakdownFoldGroup = Exclude<
  CostBreakdownGroup,
  (typeof COST_BREAKDOWN_TABLE_GROUPS)[number]
>

const TABLE_GROUPS: ReadonlySet<CostBreakdownGroup> = new Set(
  COST_BREAKDOWN_TABLE_GROUPS
)

/** Narrows the group toggle's value to the fold side of the split. */
export function isFoldGroup(
  group: CostBreakdownGroup
): group is CostBreakdownFoldGroup {
  return !TABLE_GROUPS.has(group)
}

/**
 * The unit-economics columns only real-site rows of the site group carry
 * (pseudo-sites are aggregates, not customers). Null means Rolodex was
 * unavailable (for cost_per_external_user: no external users). Cell-for-cell
 * parity with the deleted foldEconomics is pinned in breakdown.test.ts.
 */
export interface CostBreakdownRowEconomics {
  is_active: boolean | null
  documents_rolodex: number | null
  pages_rolodex: number | null
  /** Non-archived pipeline configs (the storage-churn multiplier). */
  pipeline_configs_live: number | null
  external_users: number
  llm_tokens: number
  cost_per_external_user: number | null
}

export interface CostBreakdownRow {
  key: string
  label: string
  /**
   * Raw filterable id for drill (site_id for sites; the key otherwise).
   * Null when the group is not filterable (the dimension-not-applicable
   * bucket) -- such rows are not clickable.
   */
  id: string | null
  cost: number
  share: number
  pages_processed: number | null
  pages_held: number | null
  queries: number | null
  cost_per_page_processed: number | null
  cost_per_page_held: number | null
  cost_per_query: number | null
  economics?: CostBreakdownRowEconomics
}

export interface CostBreakdownResponse {
  group_by: CostBreakdownFoldGroup
  total_cost: number
  rows: CostBreakdownRow[]
}
